import { useCallback } from "react";
import { Route, Routes, useNavigate, useParams } from "react-router";

import { getBreadcrumbsBackUrl } from "components/Breadcrumbs/helpers";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import NotFoundPage from "components/error/NotFoundPage";
import PageLoading from "components/loading/PageLoading";
import MetaInfoList from "components/MetaInfoList";
import SpacePath from "components/SpacePath";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderNavigation from "components/ViewHeader/Navigation";
import ViewHeaderScrollCollapse from "components/ViewHeader/ScrollCollapse";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import Button from "ds/components/Button";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import TabWithCounter from "ds/components/Tab/WithCounter";
import useErrorHandle from "hooks/useErrorHandle";
import useGetSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useGetSpaceliftVcsRepo";
import useSearchSpaceliftVcsFilesSuggestions from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsFilesSuggestions";
import useSearchSpaceliftVcsRepoRevisionsSuggestions from "shared/SpaceliftVcsRepo/useSearchSpaceliftVcsRepoRevisionsSuggestions";
import { downloadFileFromURL } from "utils/file";
import {
  getRepoDownloadUrl,
  getRepoRevisionsPageUrl,
  getRepoUrl,
  REPOS_PAGE_URL,
} from "utils/spaceliftRepo";
import { showDeleteSpaceliftVcsRepoConfirmation } from "views/Account/SpaceliftVcsRepos/DeleteConfirmation";
import { showEditSpaceliftVcsRepoDrawer } from "views/Account/SpaceliftVcsRepos/EditDrawer";

import { getRepoUsedByPageUrl, SPACELIFT_VCS_REPO_SORT_STORAGE_KEY } from "./helpers";
import { showRepoDetailsDrawer } from "./DetailsDrawer";
import SpaceliftVcsRepoFiles from "./Files";
import SpaceliftVcsRepoCommits from "./Commits";
import SpaceliftVcsRepoUsedBy from "./UsedBy";

const SpaceliftVcsRepo = () => {
  const { repoId } = useParams<{ repoId: string }>();
  const navigate = useNavigate();
  const { repo, loading, error } = useGetSpaceliftVcsRepo({ repoId });

  useBreadcrumbs(
    [
      {
        title: "Repos",
        link: getBreadcrumbsBackUrl(REPOS_PAGE_URL, SPACELIFT_VCS_REPO_SORT_STORAGE_KEY),
      },
      {
        title: repo?.name || "",
      },
    ],
    [repo?.name]
  );

  const { filteredCount: filesCount } = useSearchSpaceliftVcsFilesSuggestions({ repoId });
  const { filteredCount: commitsCount } = useSearchSpaceliftVcsRepoRevisionsSuggestions({
    repoId,
  });

  const handleOpenDetailsDrawer = useCallback(() => {
    if (repo) {
      showRepoDetailsDrawer({ repo });
    }
  }, [repo]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !repo) {
    return <PageLoading />;
  }

  if (!repo) {
    return <NotFoundPage />;
  }

  const repoUrl = getRepoUrl(repo.id);
  const latestRevisionSha = repo.revisions.edges[0]?.node.sha;

  const handleDownload = () => {
    if (latestRevisionSha) {
      downloadFileFromURL(getRepoDownloadUrl(repo.slug, latestRevisionSha));
    }
  };

  return (
    <>
      <ViewHeader>
        <ViewHeaderWrapper direction="row" justify="between" fullWidth>
          <ViewHeaderWrapper direction="row" align="center">
            <ViewHeaderTitle>{repo.name}</ViewHeaderTitle>
          </ViewHeaderWrapper>

          <ViewHeaderWrapper direction="row" align="end" shrink="0">
            <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
              Details
            </Button>
            <DropdownMenuEllipsis tooltip="Repository actions" dotsSize="medium">
              <CopyFieldDropdownMenuItem value={repo.id} title="Copy ID" />
              <DropdownMenuItem
                onAction={handleDownload}
                isDisabled={!latestRevisionSha}
                tooltip={!latestRevisionSha ? "No commits to download" : undefined}
                tooltipPlacement="left"
              >
                Download
              </DropdownMenuItem>
              <DropdownMenuItem onAction={() => showEditSpaceliftVcsRepoDrawer({ repo })}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onAction={() =>
                  showDeleteSpaceliftVcsRepoConfirmation({
                    id: repo.id,
                    name: repo.name,
                    onSuccess: () => navigate(REPOS_PAGE_URL),
                  })
                }
                danger
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuEllipsis>
          </ViewHeaderWrapper>
        </ViewHeaderWrapper>

        <ViewHeaderScrollCollapse>
          <MetaInfoList>
            {repo.space && (
              <SpacePath variant="meta" spaceId={repo.space.id} fallbackName={repo.space.name} />
            )}
            <TimestampMetaInfo prefix="Updated at" timestamp={repo.updatedAt} />
          </MetaInfoList>
        </ViewHeaderScrollCollapse>

        <ViewHeaderNavigation>
          <TabWithCounter label="Files" to={repoUrl} end count={filesCount} />
          <TabWithCounter
            label="Commits"
            to={getRepoRevisionsPageUrl(repo.id)}
            count={commitsCount}
          />
          <TabWithCounter
            label="Used by"
            to={getRepoUsedByPageUrl(repo.id)}
            count={repo.stacks.length}
          />
        </ViewHeaderNavigation>
      </ViewHeader>

      <Routes>
        <Route index element={<SpaceliftVcsRepoFiles repo={repo} />} />
        <Route path="commits" element={<SpaceliftVcsRepoCommits repoId={repo.id} />} />
        <Route path="used-by" element={<SpaceliftVcsRepoUsedBy stackIDs={repo.stacks} />} />
      </Routes>
    </>
  );
};

export default SpaceliftVcsRepo;
